/**
 * What do I need.
 *
 * Four questions, and then the answer an applicant cannot get anywhere else:
 * which clearances a unit of this kind needs, who issues each one, and which
 * Board will hear the application — with the section that decides it printed on
 * the screen rather than left in the Act.
 */

import { useEffect, useState } from "react";
import { bi } from "@ksidc/ontology";
import { useLang, useSession } from "@ksidc/client";
import type {
  AreasResponse,
  DiscoverResponse,
  DistrictOption,
  DistrictsResponse,
  IndustrialAreaOption,
} from "../api-types.js";
import { BOARD_NAMES, ROUTING_TEST_NAMES, days, formatRupees, inWords } from "../format.js";
import { seedDraft } from "../draft.js";
import { APPLY, navigate } from "../router.js";
import { Callout, EmptyState, ErrorNote, Loading, Panel, ProvenanceRow } from "../components/ui.js";

export function Discover(): JSX.Element {
  const { api } = useSession();
  const { lang, t } = useLang();

  const [districts, setDistricts] = useState<DistrictOption[] | null>(null);
  const [district, setDistrict] = useState("");
  const [areas, setAreas] = useState<IndustrialAreaOption[] | null>(null);
  const [inArea, setInArea] = useState<boolean | null>(null);
  const [areaId, setAreaId] = useState("");
  const [outlay, setOutlay] = useState("");
  const [result, setResult] = useState<DiscoverResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const [a, d] = await Promise.all([
          api.get<AreasResponse>("/reference/areas"),
          api.get<DistrictsResponse>("/reference/districts"),
        ]);
        if (live) {
          setAreas(a.areas);
          setDistricts(d.districts);
        }
      } catch {
        if (live) {
          setAreas([]);
          setDistricts([]);
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [api]);

  const outlayRupees = Number(outlay.replace(/[^0-9.]/g, ""));
  const outlayValid = Number.isFinite(outlayRupees) && outlayRupees > 0;
  const canAsk =
    district.length > 0 &&
    inArea !== null &&
    outlayValid &&
    (!inArea || areaId.length > 0 || (areas?.length ?? 0) === 0);

  // The areas of the chosen district only. A site in Kannur is not helped by a
  // list of parks in Ernakulam.
  const areasHere = (areas ?? []).filter((a) => a.district === district);

  const ask = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        inIndustrialArea: inArea ? "1" : "0",
        industrialAreaId: inArea ? areaId : "",
        outlayRupees: String(Math.round(outlayRupees)),
        district,
      });
      const res = await api.get<DiscoverResponse>(
        `/applications/discover/clearances?${params.toString()}`,
      );
      setResult(res);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const startApplication = (): void => {
    seedDraft({
      formA: {
        "land.inIndustrialArea": inArea === true,
        ...(inArea && areaId ? { "land.industrialAreaId": areaId } : {}),
        "land.district": district,
        "general.totalCost": String(outlayRupees / 1_00_000),
      },
      clearanceTypeIds: (result?.clearances ?? []).map((c) => c.id),
    });
    navigate(APPLY);
  };

  return (
    <div className="stack">
      <div>
        <h1>{t(bi("What do I need?", "എനിക്ക് എന്തൊക്കെ വേണം?"))}</h1>
        <p className="lede">
          {t(
            bi(
              "Four questions. Then you will see the clearances your unit needs, who issues each of them, and which Board decides your application.",
              "നാല് ചോദ്യങ്ങൾ. അതിന് ശേഷം നിങ്ങളുടെ യൂണിറ്റിന് വേണ്ട അനുമതികളും, ഓരോന്നും നൽകുന്ന അധികാരികളും, ഏത് ബോർഡാണ് അപേക്ഷ തീരുമാനിക്കുന്നത് എന്നും കാണാം.",
            ),
          )}
        </p>
      </div>

      <Panel title={bi("Your answers", "നിങ്ങളുടെ ഉത്തരങ്ങൾ")}>
        <div className="field">
          <label className="field-label" htmlFor="district">
            {t(bi("Which district is the site in?", "സ്ഥലം ഏത് ജില്ലയിലാണ്?"))}
          </label>
          <p className="field-help">
            {t(
              bi(
                "Section 4 gives every district its own Board. This decides which one hears your application, and which offices are asked for their clearance.",
                "വകുപ്പ് 4 പ്രകാരം ഓരോ ജില്ലയ്ക്കും അതിന്റേതായ ബോർഡുണ്ട്. ഏത് ബോർഡാണ് അപേക്ഷ പരിഗണിക്കുക എന്നും ഏതൊക്കെ ഓഫീസുകളോട് അനുമതി ചോദിക്കും എന്നും ഇത് തീരുമാനിക്കുന്നു.",
              ),
            )}
          </p>
          {districts === null ? (
            <Loading what={bi("Reading the districts", "ജില്ലകൾ വായിക്കുന്നു")} />
          ) : (
            <select
              id="district"
              value={district}
              onChange={(e) => {
                setDistrict(e.target.value);
                // The chosen area belongs to the old district. Clear it rather
                // than carry a park in Ernakulam into an application in Kannur.
                setAreaId("");
                setResult(null);
              }}
            >
              <option value="">{t(bi("Choose a district", "ഒരു ജില്ല തിരഞ്ഞെടുക്കുക"))}</option>
              {districts.map((d) => (
                <option key={d.district} value={d.district}>
                  {t(d.name)}
                </option>
              ))}
            </select>
          )}
        </div>

        <fieldset className="field">
          <legend className="field-label">
            {t(
              bi(
                "Is the site inside a notified industrial area or industrial park?",
                "സ്ഥലം വിജ്ഞാപനം ചെയ്ത വ്യവസായ മേഖലയിലോ വ്യവസായ പാർക്കിലോ ആണോ?",
              ),
            )}
          </legend>
          <p className="field-help">
            {t(
              bi(
                "If it is, section 7 of the Act sends your application to the Industrial Area Board for that area, however much the project costs.",
                "അങ്ങനെയെങ്കിൽ, പദ്ധതിച്ചെലവ് എത്രയായാലും വകുപ്പ് 7 പ്രകാരം അപേക്ഷ ആ മേഖലയുടെ വ്യവസായ മേഖലാ ബോർഡിന് പോകും.",
              ),
            )}
          </p>
          <div className="choice-row">
            <label className={["choice", inArea === true ? "choice-selected" : ""].join(" ")}>
              <input
                type="radio"
                name="in-area"
                checked={inArea === true}
                onChange={() => setInArea(true)}
              />
              <span>{t(bi("Yes", "അതെ"))}</span>
            </label>
            <label className={["choice", inArea === false ? "choice-selected" : ""].join(" ")}>
              <input
                type="radio"
                name="in-area"
                checked={inArea === false}
                onChange={() => {
                  setInArea(false);
                  setAreaId("");
                }}
              />
              <span>{t(bi("No", "അല്ല"))}</span>
            </label>
          </div>
        </fieldset>

        {inArea === true ? (
          <div className="field">
            <label className="field-label" htmlFor="area">
              {t(bi("Which industrial area?", "ഏത് വ്യവസായ മേഖല?"))}
            </label>
            {areas === null || districts === null ? (
              <Loading what={bi("Reading the list of areas", "മേഖലകളുടെ പട്ടിക വായിക്കുന്നു")} />
            ) : areasHere.length === 0 ? (
              <p className="field-help">
                {t(
                  bi(
                    "The list of notified industrial areas for this district has not been loaded yet. Carry on without it; the answer about which Board hears your case does not change.",
                    "ഈ ജില്ലയിലെ വിജ്ഞാപിത വ്യവസായ മേഖലകളുടെ പട്ടിക ഇതുവരെ ചേർത്തിട്ടില്ല. അത് ഇല്ലാതെ തുടരുക; ഏത് ബോർഡാണ് കേസ് പരിഗണിക്കുക എന്ന ഉത്തരം മാറില്ല.",
                  ),
                )}
              </p>
            ) : (
              <select id="area" value={areaId} onChange={(e) => setAreaId(e.target.value)}>
                <option value="">{t(bi("Choose an area", "ഒരു മേഖല തിരഞ്ഞെടുക്കുക"))}</option>
                {areasHere.map((a) => (
                  <option key={a.id} value={a.id}>
                    {t(a.name)}
                  </option>
                ))}
              </select>
            )}
          </div>
        ) : null}

        <div className="field">
          <label className="field-label" htmlFor="outlay">
            {t(bi("What will the whole project cost?", "പദ്ധതിക്ക് ആകെ എത്ര ചെലവ് വരും?"))}
          </label>
          <p className="field-help">
            {t(
              bi(
                "Add up the cost of the land, the building, the machinery and everything else, in rupees. If your site is not inside a notified industrial area, this amount decides which Board handles your application.",
                "ഭൂമി, കെട്ടിടം, യന്ത്രസാമഗ്രികൾ, മറ്റെല്ലാം ഉൾപ്പെടെ, രൂപയിൽ. സ്ഥലം വിജ്ഞാപിത മേഖലയ്ക്ക് പുറത്താണെങ്കിൽ, ഏത് ബോർഡ് അപേക്ഷ പരിഗണിക്കും എന്ന് ഈ തുകയാണ് നിർണ്ണയിക്കുന്നത്.",
              ),
            )}
          </p>
          <div className="suffix-row">
            <input
              id="outlay"
              type="number"
              inputMode="numeric"
              min="0"
              value={outlay}
              onChange={(e) => setOutlay(e.target.value)}
            />
            <span className="suffix">{t(bi("rupees", "രൂപ"))}</span>
          </div>
          {outlayValid ? (
            <p className="field-help" style={{ marginBottom: 0 }}>
              {formatRupees(outlayRupees)} — {inWords(outlayRupees, lang)}
            </p>
          ) : null}
        </div>

        <button type="button" className="btn" onClick={() => void ask()} disabled={!canAsk || busy}>
          {busy ? <span className="spinner" aria-hidden="true" /> : null}
          {t(bi("Show me what I need", "എനിക്ക് വേണ്ടത് കാണിക്കുക"))}
        </button>
      </Panel>

      {error ? <ErrorNote error={error} /> : null}

      {result ? (
        <>
          <Panel title={bi("Which Board decides your application", "ഏത് ബോർഡാണ് അപേക്ഷ തീരുമാനിക്കുന്നത്")}>
            <h3 style={{ marginTop: 0 }}>{t(BOARD_NAMES[result.routing.boardType])}</h3>
            <p className="muted">
              {t(ROUTING_TEST_NAMES[result.routing.test] ?? bi(result.routing.test, result.routing.test))}
            </p>
            <p>{t(result.routing.reason)}</p>
            <ProvenanceRow items={[result.routing.provenance]} />
          </Panel>

          {result.exemption.applies && result.exemption.text ? (
            <Callout
              tone="calm"
              title={bi(
                "You do not need some of the usual permits",
                "സാധാരണ വേണ്ട ചില അനുമതികൾ നിങ്ങൾക്ക് ആവശ്യമില്ല",
              )}
              provenance={result.exemption.provenance}
            >
              <p style={{ marginBottom: 0 }}>{t(result.exemption.text)}</p>
            </Callout>
          ) : null}

          <Panel title={bi("The clearances you need to ask for", "ആവശ്യപ്പെടേണ്ട അനുമതികൾ")}>
            <ProvenanceRow items={[result.selectionProvenance]} />
            {result.clearances.length === 0 ? (
              <div style={{ marginTop: "1rem" }}>
                <EmptyState
                  title={bi(
                    "The list of clearances has not been loaded yet",
                    "അനുമതികളുടെ പട്ടിക ഇതുവരെ ചേർത്തിട്ടില്ല",
                  )}
                  body={bi(
                    "This system keeps a list of every clearance and the office that gives it. That list has not been loaded here yet, so we cannot show it to you. You can still apply. You will choose your clearances on the form.",
                    "ഓരോ അനുമതിയും നൽകുന്ന അധികാരി ആരാണെന്ന വിവരത്തോടെയുള്ള ഒരു പട്ടിക ഈ സംവിധാനത്തിലുണ്ട്. ഈ പകർപ്പിൽ അത് ഇതുവരെ ചേർത്തിട്ടില്ല, അതിനാൽ പട്ടികപ്പെടുത്താൻ ഒന്നുമില്ല. ഏതൊക്കെ അനുമതികൾ പൈലറ്റിൽ ഉൾപ്പെടുത്തും എന്നത് കെ.എസ്.ഐ.ഡി.സിയോട് ചോദിച്ചിരിക്കുന്ന കാര്യമാണ്. നിങ്ങൾക്ക് ഇപ്പോഴും അപേക്ഷിക്കാം; അനുമതികൾ ഫോറത്തിൽ തിരഞ്ഞെടുക്കാം.",
                  )}
                />
              </div>
            ) : (
              <>
                <ul className="items" style={{ marginTop: "0.8rem" }}>
                  {result.clearances.map((c) => (
                    <li className="item" key={c.id}>
                      <div className="item-title">{t(c.name)}</div>
                      <div className="item-sub">
                        {t(bi("Issued by", "നൽകുന്നത്"))}: {t(c.authority)}
                      </div>
                      {c.statutoryTimelineDays ? (
                        <div className="item-sub notice-inline">
                          {t(
                            bi(
                              `This one has its own time limit, set by a different law: ${days(c.statutoryTimelineDays).en}, not the Board's 30 days.`,
                              `ഇതിന് മറ്റൊരു നിയമം നിശ്ചയിച്ച സ്വന്തം സമയപരിധിയുണ്ട്: ${days(c.statutoryTimelineDays).ml}, ബോർഡിന്റെ 30 ദിവസമല്ല.`,
                            ),
                          )}
                          {c.governingEnactment ? (
                            <div className="tiny muted">{c.governingEnactment}</div>
                          ) : null}
                        </div>
                      ) : c.publishedTimelineDays !== null ? (
                        <div className="item-sub">
                          {t(
                            bi(
                              `This office says it usually takes ${days(c.publishedTimelineDays).en}.`,
                              `ഈ ഓഫീസ് സാധാരണ ${days(c.publishedTimelineDays).ml} എടുക്കുമെന്ന് പറയുന്നു.`,
                            ),
                          )}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
                <hr className="rule" />
                <p className="muted">
                  {t(
                    bi(
                      "Each office sets its own target above. Those are not the time limit set by law. The legal time limit applies to the Board, and you will see it on your application page once you have applied.",
                      "ആ പ്രസിദ്ധീകരിച്ച സമയപരിധികൾ ഓരോ അധികാരിയുടേതുമാണ്. അവ നിയമപരമായ കാലാവധിയല്ല; ആ കാലാവധി ബോർഡിനെതിരെയാണ്, അപേക്ഷ നൽകിയ ശേഷം നിങ്ങളുടെ അപേക്ഷാ താളിൽ അത് കാണാം.",
                    ),
                  )}
                </p>
                <ProvenanceRow items={[result.periodProvenance]} />
              </>
            )}
          </Panel>

          <div className="btn-row no-print">
            <button type="button" className="btn" onClick={startApplication}>
              {t(bi("Start my application", "എന്റെ അപേക്ഷ ആരംഭിക്കുക"))}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}
